import { useRef, ReactNode, useEffect, useState } from "react"
import { FieldErrors } from "react-hook-form"
import { isEmpty } from "lodash-es"
import { Alert } from "@oysterhr/ui"

type FormErrorSummaryProps = {
  isVisible?: boolean
  errors?: FieldErrors
  title: string
  description: ReactNode
  className?: string
  scrollIntoView?: boolean
}

// @param errors form state errors from React Hook Form, used if isVisible is nullish
export const FormErrorSummary = ({
  isVisible,
  errors = {},
  title,
  description,
  className,
  scrollIntoView,
}: FormErrorSummaryProps) => {
  const { showErrorSummary } = useFormErrorSummary(errors)
  if (isVisible == null) isVisible = showErrorSummary

  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (scrollIntoView) {
      ref.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
    }
  }, [scrollIntoView])

  if (!isVisible) return null
  return (
    <div ref={ref}>
      <Alert className={className} variation="error">
        <Alert.Icon />
        <Alert.Heading>{title}</Alert.Heading>
        <Alert.Content data-testid="error-summary">{description}</Alert.Content>
      </Alert>
    </div>
  )
}

// @param errors form state errors from React Hook Form
export const useFormErrorSummary = (errors: FieldErrors) => {
  const [showErrorSummary, setShowErrorSummary] = useState(false)

  if (showErrorSummary && isEmpty(errors)) {
    setShowErrorSummary(false)
  } else if (!showErrorSummary && !isEmpty(errors)) {
    setShowErrorSummary(true)
  }

  return { showErrorSummary, setShowErrorSummary }
}
